import { useEffect, useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, RadioCard, Skeleton, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { changeFinancier, useCreateOptions, useFinancing, useInvalidateFinancings } from '../api';
import styles from './dialogs.module.css';

export interface ReassignDialogProps {
  financingId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * „Neu zuweisen" / „Vorschlagen" (D4): the matching candidates of the financing's deal or contact
 * (`GET /financings/create-options`) → `POST /financings/:id/financier`. Admins and team leads assign at once,
 * advisors send a proposal to the approval recipient.
 */
export function ReassignDialog({ financingId, open, onClose }: ReassignDialogProps) {
  const formId = useId();
  const { toast } = useToast();
  const invalidate = useInvalidateFinancings();
  const record = useFinancing(financingId);
  const financing = record.data;
  const options = useCreateOptions(
    open && financing ? { dealId: financing.linked.deal?.id, contactId: financing.linked.contact.id } : {},
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const mode = options.data?.financier.mode ?? null;
  const candidates = options.data?.financier.candidates ?? [];
  const currentId = financing?.financier?.id ?? null;
  const label = mode === 'propose' ? 'Vorschlagen' : 'Neu zuweisen';

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setError(null);
    setBanner(null);
  }, [open, financingId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (selected === null) {
      setError('Bitte wählen Sie einen Finanzierer.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      const result = await changeFinancier(financingId, selected);
      await invalidate(financingId);
      toast({ message: result.assigned ? 'Finanzierer zugewiesen.' : 'Vorschlag zur Freigabe gesendet.' });
      onClose();
    } catch (cause) {
      if (cause instanceof ApiError && cause.fields['financierId']) setError(cause.fields['financierId']);
      else setBanner(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  const noMode = mode === 'none' || mode === 'self';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow={financing?.numberLabel ?? 'Finanzierung'}
      title={label}
      subtitle={options.data?.financier.hint ?? financing?.title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending} disabled={noMode || candidates.length === 0}>
            {label}
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {record.isError ? <Banner tone="danger">{errorMessage(record.error)}</Banner> : null}
        {options.isError ? <Banner tone="danger">{errorMessage(options.error)}</Banner> : null}
        {noMode ? <Banner tone="info">Dafür fehlt Ihnen die Berechtigung.</Banner> : null}
        {!options.data && !options.isError ? <Skeleton variant="block" height={120} /> : null}
        {options.data && !noMode ? (
          <fieldset className={styles.group}>
            <legend className={styles.legend}>Finanzierer</legend>
            {candidates.length === 0 ? (
              <p className={styles.muted}>In der Organisation ist kein aktiver Finanzierer angelegt.</p>
            ) : null}
            <div className={styles.list}>
              {candidates.map((candidate) => (
                <RadioCard
                  key={candidate.userId}
                  name={`${formId}-financier`}
                  title={candidate.name}
                  description={[
                    candidate.userId === currentId ? 'zugewiesen' : null,
                    candidate.best ? 'beste Übereinstimmung' : null,
                    candidate.line,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  value={candidate.userId}
                  checked={selected === candidate.userId}
                  disabled={candidate.userId === currentId}
                  onChange={() => {
                    setSelected(candidate.userId);
                    setError(null);
                  }}
                />
              ))}
            </div>
            {error ? (
              <span className={styles.error} role="alert">
                {error}
              </span>
            ) : null}
          </fieldset>
        ) : null}
      </form>
    </Dialog>
  );
}
